import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckboxField,
  FormField,
  SelectField,
  Spinner,
} from "../../../components/ui";
import { ConfirmDialog } from "../../../components/ui/overlay/ConfirmDialog";
import { AppShell } from "../../../layouts/AppShell";
import {
  getQuestionnaireDraft,
  listQuestionnaireVersions,
  publishQuestionnaireDraft,
  saveQuestionnaireDraft,
} from "../api/questionnaireAdminApi";
import type { QuestionnaireDraft } from "../schemas/questionnaireSchemas";

type QuestionType = "TEXT" | "SINGLE_CHOICE" | "RATING_SCALE";

function newQuestion(type: QuestionType = "TEXT") {
  return {
    key: "",
    code: "",
    position: 0,
    type,
    prompt: "",
    required: false,
    allowOther: false,
    options:
      type === "SINGLE_CHOICE"
        ? [
            { value: "", label: "" },
            { value: "", label: "" },
          ]
        : [],
    maxLength: type === "TEXT" ? 200 : null,
    scaleMin: type === "RATING_SCALE" ? 1 : null,
    scaleMax: type === "RATING_SCALE" ? 5 : null,
    scaleMinLabel: type === "RATING_SCALE" ? "非常不同意" : null,
    scaleMaxLabel: type === "RATING_SCALE" ? "非常同意" : null,
  };
}

function validateDraft(draft: QuestionnaireDraft | null) {
  if (!draft) return "草稿尚未載入完成。";
  if (!draft.title.trim() || !draft.namedResponseNotice.trim()) {
    return "請填寫問卷標題與具名告知。";
  }
  for (const question of draft.questions) {
    if (!question.prompt.trim()) return "所有題目都需要題目文字。";
    if (
      question.type === "SINGLE_CHOICE" &&
      question.options.filter((option) => option.label.trim()).length < 2
    ) {
      return "單選題至少需要兩個選項。";
    }
    if (question.type === "TEXT" && (!question.maxLength || question.maxLength < 1)) {
      return "文字題需要有效的字數上限。";
    }
    if (
      question.type === "RATING_SCALE" &&
      (question.scaleMin ?? 0) >= (question.scaleMax ?? 0)
    ) {
      return "量表題的最小值必須小於最大值。";
    }
  }
  return undefined;
}

function toSavePayload(draft: QuestionnaireDraft) {
  return {
    title: draft.title.trim(),
    namedResponseNotice: draft.namedResponseNotice.trim(),
    questions: draft.questions.map((question) => ({
      key: question.key || undefined,
      type: question.type,
      prompt: question.prompt.trim(),
      required: question.required,
      allowOther: question.allowOther,
      options:
        question.type === "SINGLE_CHOICE"
          ? question.options
              .filter((option) => option.label.trim())
              .map((option) => ({
                value: option.value,
                label: option.label.trim(),
              }))
          : [],
      maxLength: question.type === "TEXT" ? Number(question.maxLength ?? 200) : null,
      scaleMin: question.type === "RATING_SCALE" ? Number(question.scaleMin ?? 1) : null,
      scaleMax: question.type === "RATING_SCALE" ? Number(question.scaleMax ?? 5) : null,
      scaleMinLabel:
        question.type === "RATING_SCALE" ? question.scaleMinLabel ?? "" : null,
      scaleMaxLabel:
        question.type === "RATING_SCALE" ? question.scaleMaxLabel ?? "" : null,
    })),
  };
}

function questionTypeLabel(type: QuestionType) {
  switch (type) {
    case "TEXT":
      return "文字題";
    case "SINGLE_CHOICE":
      return "單選題";
    case "RATING_SCALE":
      return "量表題";
  }
}

export function QuestionnaireDraftEditorPage() {
  const client = useQueryClient();
  const draftQuery = useQuery({
    queryKey: ["questionnaire", "draft"],
    queryFn: getQuestionnaireDraft,
  });
  const versionsQuery = useQuery({
    queryKey: ["questionnaire", "versions"],
    queryFn: listQuestionnaireVersions,
  });
  const [draftEdits, setDraftEdits] = useState<QuestionnaireDraft | null>(null);
  const [draftError, setDraftError] = useState<string>();
  const [publishOpen, setPublishOpen] = useState(false);
  const draft = draftEdits ?? draftQuery.data ?? null;

  const withDraft = (
    updater: (base: QuestionnaireDraft) => QuestionnaireDraft,
  ) => {
    setDraftEdits((current) => {
      const base = current ?? draft;
      return base ? updater(base) : current;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("draft missing");
      return saveQuestionnaireDraft(toSavePayload(draft), draft.rowVersion);
    },
    onSuccess: (data) => {
      setDraftEdits(data);
      setDraftError(undefined);
      client.setQueryData(["questionnaire", "draft"], data);
      void client.invalidateQueries({ queryKey: ["questionnaire", "versions"] });
    },
    onError: (error) => {
      setDraftError(error instanceof Error ? error.message : "草稿儲存失敗");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("draft missing");
      return publishQuestionnaireDraft(draft.rowVersion);
    },
    onSuccess: async (data) => {
      setPublishOpen(false);
      client.setQueryData(["questionnaire"], data);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["questionnaire", "draft"] }),
        client.invalidateQueries({ queryKey: ["questionnaire", "versions"] }),
      ]);
      setDraftEdits(null);
      setDraftError(undefined);
    },
    onError: (error) => {
      setDraftError(error instanceof Error ? error.message : "發布失敗");
      setPublishOpen(false);
    },
  });

  const updateQuestion = (
    index: number,
    patch: Partial<QuestionnaireDraft["questions"][number]>,
  ) => {
    withDraft((base) => {
      const questions = [...base.questions];
      const target = questions[index];
      if (!target) return base;
      questions[index] = { ...target, ...patch };
      return { ...base, questions };
    });
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    withDraft((base) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= base.questions.length) return base;
      const questions = [...base.questions];
      const currentQuestion = questions[index];
      const nextQuestion = questions[nextIndex];
      if (!currentQuestion || !nextQuestion) return base;
      questions[index] = nextQuestion;
      questions[nextIndex] = currentQuestion;
      return {
        ...base,
        questions: questions.map((question, position) => ({
          ...question,
          position: position + 1,
        })),
      };
    });
  };

  const removeQuestion = (index: number) => {
    withDraft((base) => ({
      ...base,
      questions: base.questions
        .filter((_, questionIndex) => questionIndex !== index)
        .map((question, position) => ({ ...question, position: position + 1 })),
    }));
  };

  const addQuestion = (type: QuestionType) => {
    withDraft((base) => ({
      ...base,
      questions: [
        ...base.questions,
        {
          ...newQuestion(type),
          position: base.questions.length + 1,
        },
      ],
    }));
  };

  const addOption = (questionIndex: number) => {
    withDraft((base) => {
      const questions = [...base.questions];
      const target = questions[questionIndex];
      if (!target) return base;
      questions[questionIndex] = {
        ...target,
        options: [...target.options, { value: "", label: "" }],
      };
      return { ...base, questions };
    });
  };

  const updateOption = (
    questionIndex: number,
    optionIndex: number,
    label: string,
  ) => {
    withDraft((base) => {
      const questions = [...base.questions];
      const target = questions[questionIndex];
      if (!target) return base;
      const options = [...target.options];
      const option = options[optionIndex];
      if (!option) return base;
      options[optionIndex] = { ...option, label };
      questions[questionIndex] = { ...target, options };
      return { ...base, questions };
    });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    withDraft((base) => {
      const questions = [...base.questions];
      const target = questions[questionIndex];
      if (!target) return base;
      questions[questionIndex] = {
        ...target,
        options: target.options.filter((_, index) => index !== optionIndex),
      };
      return { ...base, questions };
    });
  };

  const saveDraftNow = () => {
    const validation = validateDraft(draft);
    if (validation) {
      setDraftError(validation);
      return;
    }
    saveMutation.mutate();
  };

  const publishDraftNow = () => {
    const validation = validateDraft(draft);
    if (validation) {
      setDraftError(validation);
      return;
    }
    publishMutation.mutate();
  };

  return (
    <AppShell title="問卷草稿編輯" breadcrumb={["管理後台", "固定問卷", "草稿編輯"]}>
      <div className="questionnaire-draft-page">
        {draftQuery.isLoading && <Spinner label="載入草稿" />}
        {draft && (
          <>
            <Card title="草稿總覽">
              <div className="questionnaire-draft-summary row">
                <div className="col-xl-8 mb-3 mb-xl-0">
                  <div className="questionnaire-draft-summary-panel">
                    <div className="questionnaire-draft-summary-item">
                      <span className="questionnaire-draft-summary-label">目前草稿基底</span>
                      <strong>
                        {draft.lastPublishedVersionNumber
                          ? `V${draft.lastPublishedVersionNumber}`
                          : "尚未發布正式版本"}
                      </strong>
                    </div>
                    <div className="questionnaire-draft-summary-item">
                      <span className="questionnaire-draft-summary-label">題目數量</span>
                      <strong>{draft.questions.length} 題</strong>
                    </div>
                    <div className="questionnaire-draft-summary-item">
                      <span className="questionnaire-draft-summary-label">編輯提醒</span>
                      <span>發布後會產生新版本，並預設維持截止狀態。</span>
                    </div>
                  </div>
                </div>
                <div className="col-xl-4">
                  <div className="questionnaire-draft-toolbar">
                    <a className="btn btn-outline-secondary" href="/admin/questionnaire">
                      返回問卷管理
                    </a>
                    <Button
                      variant="secondary"
                      loading={saveMutation.isPending}
                      onClick={saveDraftNow}
                    >
                      儲存草稿
                    </Button>
                    <Button variant="success" onClick={() => setPublishOpen(true)}>
                      發布新版本
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="問卷設定">
              <Alert variant="info">
                先在草稿調整題目與排序，確認後再發布；發布後舊填答仍可查閱，但不再列入現行版本統計。
              </Alert>
              <div className="row">
                <div className="col-lg-5">
                  <FormField
                    label="問卷標題"
                    value={draft.title}
                    onChange={(event) =>
                      withDraft((base) => ({ ...base, title: event.target.value }))
                    }
                  />
                </div>
                <div className="col-lg-7">
                  <FormField
                    label="具名填答告知"
                    value={draft.namedResponseNotice}
                    onChange={(event) =>
                      withDraft((base) => ({
                        ...base,
                        namedResponseNotice: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </Card>

            <Card title="題目編排">
              <div className="questionnaire-draft-action-bar">
                <span className="text-muted">新增題目</span>
                <div className="questionnaire-draft-inline-actions">
                  <Button variant="secondary" className="btn-sm" onClick={() => addQuestion("TEXT")}>
                    文字題
                  </Button>
                  <Button variant="secondary" className="btn-sm" onClick={() => addQuestion("SINGLE_CHOICE")}>
                    單選題
                  </Button>
                  <Button variant="secondary" className="btn-sm" onClick={() => addQuestion("RATING_SCALE")}>
                    量表題
                  </Button>
                </div>
              </div>

              <div className="questionnaire-draft-question-list">
                {draft.questions.map((question, index) => (
                  <section
                    className="questionnaire-draft-question"
                    key={`${question.key || "new"}-${index}`}
                  >
                    <div className="questionnaire-draft-question-header">
                      <div>
                        <div className="questionnaire-draft-question-title-row">
                          <h2 className="h5 mb-1">題目 {index + 1}</h2>
                          <Badge variant="primary">{questionTypeLabel(question.type)}</Badge>
                          {question.required && <Badge variant="warning">必填</Badge>}
                        </div>
                        <p className="text-muted mb-0">
                          依序設定題型、題目內容與作答規則。
                        </p>
                      </div>
                      <div className="questionnaire-draft-inline-actions">
                        <Button
                          variant="secondary"
                          className="btn-sm"
                          disabled={index === 0}
                          onClick={() => moveQuestion(index, -1)}
                        >
                          上移
                        </Button>
                        <Button
                          variant="secondary"
                          className="btn-sm"
                          disabled={index === draft.questions.length - 1}
                          onClick={() => moveQuestion(index, 1)}
                        >
                          下移
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-sm"
                          onClick={() => removeQuestion(index)}
                        >
                          刪除題目
                        </Button>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-lg-8">
                        <FormField
                          label="題目文字"
                          value={question.prompt}
                          onChange={(event) =>
                            updateQuestion(index, { prompt: event.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4 col-lg-2">
                        <SelectField
                          label="題型"
                          value={question.type}
                          onChange={(event) =>
                            updateQuestion(
                              index,
                              newQuestion(event.target.value as QuestionType),
                            )
                          }
                        >
                          <option value="TEXT">文字題</option>
                          <option value="SINGLE_CHOICE">單選題</option>
                          <option value="RATING_SCALE">量表題</option>
                        </SelectField>
                      </div>
                      <div className="col-md-4 col-lg-2 d-flex align-items-end">
                        <div className="questionnaire-draft-checkbox-wrap">
                          <CheckboxField
                            label="設為必填"
                            checked={question.required}
                            onChange={(event) =>
                              updateQuestion(index, { required: event.target.checked })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {question.type === "TEXT" && (
                      <div className="row">
                        <div className="col-md-4 col-lg-3">
                          <FormField
                            label="字數上限"
                            type="number"
                            value={String(question.maxLength ?? 200)}
                            onChange={(event) =>
                              updateQuestion(index, {
                                maxLength: Number(event.target.value),
                              })
                            }
                          />
                        </div>
                      </div>
                    )}

                    {question.type === "RATING_SCALE" && (
                      <div className="row">
                        <div className="col-md-3">
                          <FormField
                            label="量表最小值"
                            type="number"
                            value={String(question.scaleMin ?? 1)}
                            onChange={(event) =>
                              updateQuestion(index, {
                                scaleMin: Number(event.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <FormField
                            label="量表最大值"
                            type="number"
                            value={String(question.scaleMax ?? 5)}
                            onChange={(event) =>
                              updateQuestion(index, {
                                scaleMax: Number(event.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <FormField
                            label="最小值標籤"
                            value={question.scaleMinLabel ?? ""}
                            onChange={(event) =>
                              updateQuestion(index, {
                                scaleMinLabel: event.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <FormField
                            label="最大值標籤"
                            value={question.scaleMaxLabel ?? ""}
                            onChange={(event) =>
                              updateQuestion(index, {
                                scaleMaxLabel: event.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}

                    {question.type === "SINGLE_CHOICE" && (
                      <div className="questionnaire-draft-options">
                        <div className="questionnaire-draft-options-header">
                          <div>
                            <h3 className="h6 mb-1 text-primary">選項設定</h3>
                            <p className="text-muted mb-0">至少保留兩個有效選項。</p>
                          </div>
                          <div className="questionnaire-draft-inline-actions">
                            <div className="questionnaire-draft-checkbox-wrap mb-0">
                              <CheckboxField
                                label="包含其他選項"
                                checked={question.allowOther}
                                onChange={(event) =>
                                  updateQuestion(index, {
                                    allowOther: event.target.checked,
                                  })
                                }
                              />
                            </div>
                            <Button
                              variant="secondary"
                              className="btn-sm"
                              onClick={() => addOption(index)}
                            >
                              新增選項
                            </Button>
                          </div>
                        </div>
                        <div className="row">
                          {question.options.map((option, optionIndex) => (
                            <div className="col-lg-6" key={`${option.value}-${optionIndex}`}>
                              <div className="questionnaire-draft-option-card">
                                <div className="questionnaire-draft-option-title-row">
                                  <span className="font-weight-bold">選項 {optionIndex + 1}</span>
                                  <Button
                                    variant="danger"
                                    className="btn-sm"
                                    onClick={() => removeOption(index, optionIndex)}
                                  >
                                    刪除
                                  </Button>
                                </div>
                                <FormField
                                  label="顯示文字"
                                  value={option.label}
                                  onChange={(event) =>
                                    updateOption(index, optionIndex, event.target.value)
                                  }
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                ))}
              </div>

              {draftError && <Alert variant="danger">{draftError}</Alert>}

              <div className="questionnaire-draft-footer-actions">
                <Button
                  variant="secondary"
                  loading={saveMutation.isPending}
                  onClick={saveDraftNow}
                >
                  儲存草稿
                </Button>
                <Button variant="success" onClick={() => setPublishOpen(true)}>
                  發布新版本
                </Button>
              </div>
            </Card>

            <Card title="版本紀錄">
              {versionsQuery.isLoading && <Spinner label="載入版本" />}
              <div className="questionnaire-draft-version-list">
                {(versionsQuery.data ?? []).map((version) => (
                  <div className="questionnaire-draft-version-item" key={version.id}>
                    <strong>V{version.versionNumber}</strong>
                    <span>{version.title}</span>
                    <span className="text-muted">
                      {new Date(version.publishedAt).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
      <ConfirmDialog
        open={publishOpen}
        title="發布新版本"
        description="發布後舊填答會保留封存，但學生在新版本下會重新視為未填答。新版本會先維持截止狀態。"
        confirmLabel="確認發布"
        loading={publishMutation.isPending}
        onClose={() => setPublishOpen(false)}
        onConfirm={publishDraftNow}
      />
    </AppShell>
  );
}
